import { useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useFetch } from "../../../hooks/api";
import Card from "../../../components/core/basic/Card";
import { Button } from "../../../components/core/basic/Button";
import axios from "axios";
import FileSaver from "file-saver";
import { getURLPrefix } from "../../../utils/getUrlPrefix";
import { AnalyticsStatsPanel } from "../../../components/custom/analytics/AnalyticsStatsPanel";
import AnalyticsOverviewChart from "../../../components/custom/analytics/AnalyticsOverviewChart";
import { useDates } from "../../../dates/DatesContext";
import { FaFileExcel } from "react-icons/fa6";
import { Table } from "../../../components/table/Table";
import { useClientTable } from "../../../components/table/tableFeatures";
import { createColumnBuilder } from "../../../components/table/columnBuilder";
import { formatPercent } from "../../../utils/formatNumber";
import { HStack, VStack } from "../../../../styled-system/jsx";
import { resetTable } from "../../../components/table/utils";
import { MdRotateLeft } from "react-icons/md";
import { RT8ColumnSelector } from "../../../components/core/blocks/MetricSelector";
import { SearchInput } from "../../../components/fields/base/TextField";
import { css } from "../../../../styled-system/css";
import { RT8Pagination } from "../../../components/table/Pagination";
import { RT8FilterWidget } from "../../merchandising/components/tables/FilterWidget";
import { customFilterFn } from "../../../components/table/filterFns";

const URL_PREFIX = getURLPrefix();

const columnHelper = createColumnBuilder();

const AnalyticsOverview = ({ shelfRedirect, marketplaceRedirect }) => {
  const { t } = useTranslation(["advertising", "common"]);
  const { start, end } = useDates();

  const onShelfClick = useCallback(
    (row) => {
      shelfRedirect(row.original.id);
      marketplaceRedirect(row.original.marketplace);
    },
    [marketplaceRedirect, shelfRedirect],
  );

  const { data: shelfOverview } = useFetch(["shelf_stats_overview"], "/api/shelf_stats/", {
    stats_by: "shelf",
    end: end.toISOString(),
    start: start.toISOString(),
  });

  const handleExport = async () => {
    const url = URL_PREFIX + "/api/shelf_stats/";

    const params = {
      end: end.toISOString(),
      start: start.toISOString(),
      report: true,
      stats_by: "shelf",
    };

    const response = await axios({
      method: "GET",
      url: url,
      responseType: "blob",
      params: params,
    });

    const blob = new Blob([response.data], {
      type: "vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8",
    });

    FileSaver.saveAs(blob, "report.xlsx");
  };

  const tableData = useMemo(() => {
    const formattedData = [];

    for (const [key, value] of Object.entries(shelfOverview?.data ?? {})) {
      formattedData.push({
        id: key,
        name: value.page_1?.name || value.above_fold?.name,
        marketplace: value.page_1?.marketplace || value.above_fold?.marketplace,
        page_1_sos: value.page_1?.share_of_shelf || 0,
        page_1_sos_org: value.page_1?.org_share_of_shelf || 0,
        page_1_sov: value.page_1?.share_of_voice || 0,
        above_fold_sos: value.above_fold?.share_of_shelf || 0,
        above_fold_sos_org: value.above_fold?.org_share_of_shelf || 0,
        above_fold_sov: value.above_fold?.share_of_voice || 0,
        average_position: value.page_1?.position_avg || 0,
      });
    }

    return formattedData;
  }, [shelfOverview]);

  const columns = useMemo(
    () => [
      columnHelper.accessor("marketplace", {
        header: t(($) => $.advertising["Market Place"]),
        size: 100,
        meta: {
          columnFilterOptions: {
            format: "text",
          },
        },
      }),
      columnHelper.accessor("name", {
        header: t(($) => $.advertising["Shelf"]),
        size: 130,
        cell: ({ getValue, row }) => (
          <Button onPress={() => onShelfClick(row)} variant="link">
            {getValue()}
          </Button>
        ),
        meta: {
          columnFilterOptions: {
            format: "text",
          },
        },
      }),
      columnHelper.accessor("page_1_sos", {
        header: t(($) => $.advertising["SOS"]),
        size: 130,
        cell: ({ getValue }) => {
          return formatPercent(getValue() / 100, {
            decimalPlaces: 1,
          });
        },
        enableGlobalFilter: false,
        meta: {
          columnFilterOptions: {
            format: "percent",
          },
        },
      }),
      columnHelper.accessor("page_1_sos_org", {
        header: t(($) => $.advertising["SOS Organic"]),
        size: 130,
        cell: ({ getValue }) => {
          return formatPercent(getValue() / 100, {
            decimalPlaces: 1,
          });
        },
        enableGlobalFilter: false,
        meta: {
          columnFilterOptions: {
            format: "percent",
          },
        },
      }),
      columnHelper.accessor("page_1_sov", {
        header: t(($) => $.advertising["SOV"]),
        size: 130,
        cell: ({ getValue }) => {
          return formatPercent(getValue() / 100, {
            decimalPlaces: 1,
          });
        },
        enableGlobalFilter: false,
        meta: {
          columnFilterOptions: {
            format: "percent",
          },
        },
      }),
      columnHelper.accessor("above_fold_sos", {
        header: t(($) => $.advertising["Above the Fold SOS"]),
        size: 130,
        cell: ({ getValue }) => {
          return formatPercent(getValue() / 100, {
            decimalPlaces: 1,
          });
        },
        enableGlobalFilter: false,
        meta: {
          columnFilterOptions: {
            format: "percent",
          },
        },
      }),
      columnHelper.accessor("above_fold_sos_org", {
        header: t(($) => $.advertising["Above the Fold SOS Organic"]),
        size: 130,
        cell: ({ getValue }) => {
          return formatPercent(getValue() / 100, {
            decimalPlaces: 1,
          });
        },
        enableGlobalFilter: false,
        meta: {
          columnFilterOptions: {
            format: "percent",
          },
        },
      }),
      columnHelper.accessor("above_fold_sov", {
        header: t(($) => $.advertising["Above the Fold SOV"]),
        width: 180,
        cell: ({ getValue }) => {
          return formatPercent(getValue() / 100, {
            decimalPlaces: 1,
          });
        },
        enableGlobalFilter: false,
        meta: {
          columnFilterOptions: {
            format: "percent",
          },
        },
      }),
      columnHelper.accessor("average_position", {
        header: t(($) => $.advertising["Average Position"]),
        size: 130,
        cell: ({ getValue }) => {
          return Math.round(getValue());
        },
        enableGlobalFilter: false,
        meta: {
          columnFilterOptions: {
            format: "number",
          },
        },
      }),
    ],
    [onShelfClick, t],
  );

  const table = useClientTable({
    data: tableData,
    columns,
    defaultColumn: {
      filterFn: customFilterFn,
    },
    initialState: { pagination: { pageIndex: 0, pageSize: 10 } },
  });

  return (
    <>
      <AnalyticsStatsPanel />
      <Card>
        <AnalyticsOverviewChart />
        <HStack padding={"small"} fontSize={"large"}>
          <VStack flex="1" alignItems={"start"}>
            <HStack width={"100%"}>
              <Button
                variant={"contentOnly"}
                onPress={() => {
                  resetTable(table);
                }}
                fontSize={"xxlarge"}
              >
                <MdRotateLeft />
              </Button>
              <RT8ColumnSelector table={table} />
              <SearchInput
                aria-label={t(($) => $.common.actions["Search"])}
                isExpandable
                css={{ flex: "0 1 350px" }}
                value={table.state.globalFilter}
                onChange={table.setGlobalFilter}
              />
            </HStack>
            <RT8FilterWidget table={table} />
          </VStack>
          <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
        </HStack>
        <Table table={table} />
        <Button onPress={handleExport} className={css({ marginTop: "medium" })}>
          <FaFileExcel /> {t(($) => $.advertising["Download Report"])}
        </Button>
      </Card>
    </>
  );
};

export default AnalyticsOverview;
